import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Snowflake, ArrowRight, Lock, User } from "lucide-react";

const LoginPage = () => {
    const { login, error } = useAuth();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        await login(username.trim(), password);
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
            {/* Left — form */}
            <div className="flex items-center justify-center p-8 md:p-12 bg-white">
                <div className="w-full max-w-md animate-fade-up">
                    <div className="flex items-center gap-2 mb-12">
                        <div className="w-9 h-9 bg-black flex items-center justify-center">
                            <Snowflake className="w-5 h-5 text-white" strokeWidth={1.5} />
                        </div>
                        <div>
                            <div className="font-display text-lg font-black tracking-tight leading-none">LC SOĞUTMA</div>
                            <div className="tag-label leading-none mt-1">Bayi Paneli</div>
                        </div>
                    </div>

                    <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tight mb-3 leading-[0.95]">
                        Operasyona<br />giriş yapın.
                    </h1>
                    <p className="text-zinc-600 text-base mb-10 leading-relaxed max-w-sm">
                        Sipariş ve garanti süreçlerinizi tek panelden yönetin.
                    </p>

                    <form onSubmit={onSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label className="tag-label" htmlFor="username">Kullanıcı Adı</Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                                <Input
                                    id="username"
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    autoComplete="username"
                                    required
                                    className="rounded-none h-12 pl-10 border-zinc-300 focus-visible:ring-0 focus-visible:border-black"
                                    placeholder="kullanıcı adınız"
                                    data-testid="login-username-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="tag-label" htmlFor="password">Şifre</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" strokeWidth={1.5} />
                                <Input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                    required
                                    className="rounded-none h-12 pl-10 border-zinc-300 focus-visible:ring-0 focus-visible:border-black"
                                    placeholder="••••••••"
                                    data-testid="login-password-input"
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="border border-red-500 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="login-error">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={submitting}
                            className="w-full h-12 rounded-none bg-black hover:bg-zinc-800 text-white font-semibold tracking-wide group"
                            data-testid="login-submit-button"
                        >
                            {submitting ? "Giriş yapılıyor..." : "GİRİŞ YAP"}
                            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" strokeWidth={2} />
                        </Button>
                    </form>

                    <div className="mt-12 pt-6 border-t border-zinc-200">
                        <p className="text-xs text-zinc-500 leading-relaxed">
                            Bayi hesabınız yoksa lütfen yöneticinizle iletişime geçin. Yetkisiz erişim girişimleri kayıt altına alınır.
                        </p>
                    </div>
                </div>
            </div>

            {/* Right — visual */}
            <div className="hidden lg:flex relative bg-zinc-100 grid-pattern items-center justify-center p-12 border-l border-zinc-200">
                <div className="absolute top-8 left-8 right-8 flex justify-between items-start text-xs font-mono uppercase tracking-[0.15em] text-zinc-500">
                    <span>SYS://lc-sogutma/v1</span>
                    <span>● ÇEVRİMİÇİ</span>
                </div>

                <div className="absolute bottom-8 left-8 right-8 flex justify-between items-end text-xs font-mono uppercase tracking-[0.15em] text-zinc-500">
                    <span>{new Date().toLocaleDateString("tr-TR")}</span>
                    <span>BAYI / ADMIN</span>
                </div>

                <div className="relative max-w-md text-center">
                    <div className="inline-flex items-center justify-center w-32 h-32 border border-zinc-300 bg-white mb-8">
                        <Snowflake className="w-16 h-16 text-black" strokeWidth={1} />
                    </div>
                    <div className="font-display text-3xl font-black tracking-tight mb-4 leading-tight">
                        Mobil Klima<br />Çözüm Ortağınız.
                    </div>
                    <p className="text-zinc-600 text-sm leading-relaxed">
                        Araç klima sistemlerinizin sipariş ve garanti operasyonlarını profesyonel düzeyde yönetin.
                    </p>
                    <div className="grid grid-cols-3 gap-px bg-zinc-300 mt-12 border border-zinc-300">
                        {["AC200EQ", "AC300EQ", "AC400EQ"].map((m) => (
                            <div key={m} className="bg-white p-4 text-center">
                                <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">SERİ</div>
                                <div className="font-display font-bold text-sm">{m}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
